import {gql} from '@apollo/client';
import {groupBy} from '@benchling/js-utils';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import type {
  PMEISidepanelDetailQuery,
  PMEISidepanelDetailQuery_procedureMethodExecutionInstance_ProcedureMethodExecutionInstanceNode as PMEINode,
  PMEISidepanelDetailQuery_procedureMethodExecutionInstance_ProcedureMethodExecutionInstanceNode_templateMethodVersion_ProcedureMethodDefinitionVersionNode as TemplateMethodVersionNode,
  PMEISidepanelDetailQueryVariables,
} from './__generated/PMEISidepanelDetailQuery';

export const PMEI_SIDEPANEL_DETAIL_QUERY = gql`
  query PMEISidepanelDetailQuery($pmeiId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureMethodExecutionInstance(id: $pmeiId) {
      ... on ProcedureMethodExecutionInstanceNode {
        id
        worksheet {
          ... on WorksheetNode {
            id
            name
            displayId
            editUrl
          }
          ...InaccessibleInfoGraphQL
        }
        templateMethodVersion {
          ... on ProcedureMethodDefinitionVersionNode {
            id
            name
            dataParameterSettings {
              version {
                id
                name
                dataType
              }
            }
            materialInputSettings {
              version {
                id
                name
                dataType
              }
            }
            materialOutputSettings {
              version {
                id
                name
                dataType
              }
            }
            equipmentSettings {
              version {
                id
                name
                dataType
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        dataParameterPlannedValues {
          id
          value
          parameterVersion {
            id
          }
        }
        materialInputPlannedValues {
          id
          material
          amount
          parameterVersion {
            id
          }
        }
        equipmentPlannedValues {
          id
          equipment
          parameterVersion {
            id
          }
        }
        dataParameterConfirmationValues {
          id
          value
          comment
          parameterVersion {
            id
          }
        }
        dataParameterActualValues {
          id
          value
          comment
          parameterVersion {
            id
          }
        }
        materialInputConfirmationValues {
          id
          material
          amount
          comment
          parameterVersion {
            id
          }
        }
        materialOutputConfirmationValues {
          id
          material
          comment
          materialOutputVersion {
            id
            name
          }
        }
        equipmentConfirmationValues {
          id
          equipment
          comment
          parameterVersion {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface PMEISidepanelDetailData {
  worksheetUrl: string | null;
  worksheetDisplayId: string | null;
  parameterData: PMEIParameterData | null;
}

export interface PMEISidepanelDataParameterRow {
  version: TemplateMethodVersionNode['dataParameterSettings'][number]['version'];
  plannedValues: Array<PMEINode['dataParameterPlannedValues'][number]>;
  confirmationValues: Array<PMEINode['dataParameterConfirmationValues'][number]>;
  actualValues: Array<PMEINode['dataParameterActualValues'][number]>;
}

export interface PMEISidepanelMaterialInputRow {
  version: TemplateMethodVersionNode['materialInputSettings'][number]['version'];
  plannedValues: Array<PMEINode['materialInputPlannedValues'][number]>;
  confirmationValues: Array<PMEINode['materialInputConfirmationValues'][number]>;
}

export interface PMEISidepanelMaterialOutputRow {
  version: TemplateMethodVersionNode['materialOutputSettings'][number]['version'];
  confirmationValues: Array<PMEINode['materialOutputConfirmationValues'][number]>;
}

export interface PMEISidepanelEquipmentRow {
  version: TemplateMethodVersionNode['equipmentSettings'][number]['version'];
  plannedValues: Array<PMEINode['equipmentPlannedValues'][number]>;
  confirmationValues: Array<PMEINode['equipmentConfirmationValues'][number]>;
}

export interface PMEIParameterData {
  dataParameters: Array<PMEISidepanelDataParameterRow>;
  materialInputs: Array<PMEISidepanelMaterialInputRow>;
  materialOutputs: Array<PMEISidepanelMaterialOutputRow>;
  equipment: Array<PMEISidepanelEquipmentRow>;
}

function extractParameterData(pmei: PMEINode): PMEIParameterData | null {
  const tmv = pmei.templateMethodVersion;
  if (tmv == null || isInaccessible(tmv)) {
    return null;
  }

  const dpPlanned = groupBy(pmei.dataParameterPlannedValues, (v) => v.parameterVersion.id);
  const dpConfirmed = groupBy(pmei.dataParameterConfirmationValues, (v) => v.parameterVersion.id);
  const dpActual = groupBy(pmei.dataParameterActualValues, (v) => v.parameterVersion.id);
  const miPlanned = groupBy(pmei.materialInputPlannedValues, (v) => v.parameterVersion.id);
  const miConfirmed = groupBy(pmei.materialInputConfirmationValues, (v) => v.parameterVersion.id);
  const moConfirmed = groupBy(pmei.materialOutputConfirmationValues, (v) => v.materialOutputVersion.id);
  const eqPlanned = groupBy(pmei.equipmentPlannedValues, (v) => v.parameterVersion.id);
  const eqConfirmed = groupBy(pmei.equipmentConfirmationValues, (v) => v.parameterVersion.id);

  return {
    dataParameters: tmv.dataParameterSettings.map((s) => ({
      version: s.version,
      plannedValues: dpPlanned[s.version.id] ?? [],
      confirmationValues: dpConfirmed[s.version.id] ?? [],
      actualValues: dpActual[s.version.id] ?? [],
    })),
    materialInputs: tmv.materialInputSettings.map((s) => ({
      version: s.version,
      plannedValues: miPlanned[s.version.id] ?? [],
      confirmationValues: miConfirmed[s.version.id] ?? [],
    })),
    materialOutputs: tmv.materialOutputSettings.map((s) => ({
      version: s.version,
      confirmationValues: moConfirmed[s.version.id] ?? [],
    })),
    equipment: tmv.equipmentSettings.map((s) => ({
      version: s.version,
      plannedValues: eqPlanned[s.version.id] ?? [],
      confirmationValues: eqConfirmed[s.version.id] ?? [],
    })),
  };
}

function buildPMEISidepanelDetailData(
  pmei: PMEISidepanelDetailQuery['procedureMethodExecutionInstance']
): PMEISidepanelDetailData | null {
  if (isInaccessible(pmei)) {
    return null;
  }

  const worksheet = pmei.worksheet;
  const hasWorksheet = !isInaccessible(worksheet);

  return {
    worksheetUrl: hasWorksheet ? worksheet.editUrl : null,
    worksheetDisplayId: hasWorksheet ? (worksheet.displayId ?? worksheet.name) : null,
    parameterData: extractParameterData(pmei),
  };
}

export default function usePMEISidepanelDetail(pmeiId: string): {
  loading: boolean;
  data: PMEISidepanelDetailData | null;
} {
  const result = useBenchlingQuery<PMEISidepanelDetailQuery, PMEISidepanelDetailQueryVariables>(
    PMEI_SIDEPANEL_DETAIL_QUERY,
    {
      variables: {pmeiId},
    }
  );

  if (result.type === QueryResultType.PENDING) {
    return {loading: true, data: null};
  }

  if (result.type === QueryResultType.ERROR) {
    return {loading: false, data: null};
  }

  return {
    loading: false,
    data: buildPMEISidepanelDetailData(result.data.procedureMethodExecutionInstance),
  };
}
